import { Alert, Button, Card, Form, Input, Select, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  student_id: string;
  id_kind: string;
  status_notes?: string;
};

export default function IdsWorkflowFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const location = useLocation();
  const presetStudentId = (location.state as { student_id?: string } | null)?.student_id;
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.ids.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<{ id: string }>('/education/ids/workflows', values),
    onSuccess: (res) => {
      message.success(t('idWorkflowCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/ids/workflows/${id}` : '/education/ids/workflows');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newIdWorkflow')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newIdWorkflow')}
      description={t('idWorkflowsIntro')}
      extra={<Button onClick={() => navigate('/education/ids/workflows')}>{t('idWorkflows')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('idsExternalHint')} />
        <Form
          layout="vertical"
          initialValues={{ student_id: presetStudentId }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="id_kind" label={t('idKind')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'pen', label: 'PEN' },
                { value: 'apaar', label: 'APAAR' },
              ]}
            />
          </Form.Item>
          <Form.Item name="status_notes" label={t('notes')}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
